import React, { useState, useEffect, Component } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { landingPagesAPI, aiAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Plus, Edit2, Trash2, Eye, Users, ExternalLink, 
  Copy, Loader2, Lock, FileText, Palette, Sparkles, Image as ImageIcon, Wand2, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

// Error Boundary to catch render errors
class LandingPagesErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('LandingPages Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-center p-4">
          <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Something went wrong</h2>
          <p className="text-gray-400 mb-4">There was an error loading this page.</p>
          <Button 
            onClick={() => {
              this.setState({ hasError: false, error: null });
              window.location.reload();
            }}
            className="bg-neon-mint text-black"
          >
            Reload Page
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

const LandingPages = () => {
  const { user } = useAuth();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    headline: '',
    subheadline: '',
    description: '',
    cta_text: 'Get Started',
    hero_image_url: '',
    video_url: '',
    theme: 'dark',
    collect_phone: false,
    thank_you_message: 'Thanks for signing up! We\'ll be in touch soon.',
    redirect_url: ''
  });

  const isPaidPlan = user?.plan === 'pro' || user?.plan === 'power';
  const isPowerPlan = user?.plan === 'power';

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const response = await landingPagesAPI.getAll();
      setPages(response.data.landing_pages);
    } catch (error) {
      toast.error('Failed to load landing pages');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      headline: '',
      subheadline: '',
      description: '',
      cta_text: 'Get Started',
      hero_image_url: '',
      video_url: '',
      theme: 'dark',
      collect_phone: false,
      thank_you_message: 'Thanks for signing up! We\'ll be in touch soon.',
      redirect_url: ''
    });
    setEditingPage(null);
  };

  const openCreateDialog = () => {
    if (pages.length >= maxPages) {
      toast.error(`You've reached your capture page limit (${maxPages}). Upgrade for more.`);
      return;
    }
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (page) => {
    setEditingPage(page);
    setFormData({
      title: page.title,
      headline: page.headline,
      subheadline: page.subheadline || '',
      description: page.description,
      cta_text: page.cta_text,
      hero_image_url: page.hero_image_url || '',
      video_url: page.video_url || '',
      theme: page.theme,
      collect_phone: page.collect_phone,
      thank_you_message: page.thank_you_message,
      redirect_url: page.redirect_url || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (editingPage) {
        await landingPagesAPI.update(editingPage.id, formData);
        toast.success('Landing page updated!');
      } else {
        await landingPagesAPI.create(formData);
        toast.success('Landing page created!');
      }
      setDialogOpen(false);
      resetForm();
      fetchPages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save landing page');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this landing page? All leads will also be deleted.')) return;
    
    try {
      await landingPagesAPI.delete(id);
      toast.success('Landing page deleted');
      fetchPages();
    } catch (error) {
      toast.error('Failed to delete landing page');
    }
  };

  const copyPageLink = (slug) => {
    const link = `${window.location.origin}/lp/${slug}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied!');
  };

  const generateAIContent = async () => {
    if (!aiTopic.trim()) {
      toast.error('Please enter a topic first');
      return;
    }
    
    setGeneratingContent(true);
    try {
      const response = await aiAPI.generateLandingContent(aiTopic);
      // Backend returns content directly, not wrapped in 'content' key
      const content = response.data;
      setFormData(prev => ({
        ...prev,
        headline: content.headline || prev.headline,
        subheadline: content.subheadline || prev.subheadline,
        description: content.description || prev.description,
        cta_text: content.cta_text || content.cta || prev.cta_text,
        title: prev.title || aiTopic.slice(0, 50)
      }));
      toast.success('AI content generated!');
    } catch (error) {
      console.error('AI content error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate content');
    } finally {
      setGeneratingContent(false);
    }
  };

  const generateAIImage = async () => {
    if (!aiTopic.trim() && !formData.headline.trim() && !formData.title.trim()) {
      toast.error('Please enter a topic or headline first');
      return;
    }
    
    setGeneratingImage(true);
    try {
      const businessName = formData.title || aiTopic || 'My Business';
      const businessType = aiTopic || formData.headline || 'professional service';
      
      const response = await aiAPI.generateLandingImage(businessName, businessType, 'professional');
      
      if (response && response.data && response.data.full_image) {
        const imageData = response.data.full_image;
        const mimeType = response.data.mime_type || 'image/png';
        
        // Use data URL directly - simpler and more reliable
        const imageUrl = `data:${mimeType};base64,${imageData}`;
        
        // Validate the image can load before setting state
        const img = new Image();
        img.onload = () => {
          setFormData(prev => ({ ...prev, hero_image_url: imageUrl }));
          toast.success('AI image generated!');
          setGeneratingImage(false);
        };
        img.onerror = () => {
          toast.error('Generated image failed to load');
          setGeneratingImage(false);
        };
        img.src = imageUrl;
        return; // Don't call setGeneratingImage in finally
      } else {
        toast.error('No image was returned from AI');
      }
    } catch (error) {
      console.error('Image generation error:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to generate image';
      toast.error(errorMessage);
    }
    setGeneratingImage(false);
  };

  const planLimits = { free: 1, pro: 5, power: 10 };
  const maxPages = planLimits[user?.plan] || 1;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-neon-mint border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="landing-pages">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-white flex items-center gap-2">
            <FileText className="w-6 h-6 text-neon-violet" />
            Landing Pages
          </h1>
          <p className="text-gray-400">
            {`${pages.length} / ${maxPages} capture pages (${user?.plan || 'free'} plan)`}
          </p>
        </div>
        <Button 
          onClick={openCreateDialog}
          disabled={pages.length >= maxPages}
          className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold shadow-neon btn-press"
          data-testid="create-landing-page-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Capture Page
        </Button>
      </div>

      {/* Upgrade Banner - show when at limit */}
      {pages.length >= maxPages && user?.plan !== 'power' && (
        <Card className="bg-gradient-to-r from-neon-violet/20 to-neon-mint/20 border-neon-violet/30">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-neon-violet/20 flex items-center justify-center">
                <Lock className="w-6 h-6 text-neon-violet" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-semibold text-lg text-white">Need More Capture Pages?</h3>
                <p className="text-gray-400 text-sm">
                  Upgrade your plan to create more capture pages and unlock advanced features.
                </p>
              </div>
              <Button className="bg-neon-violet text-white hover:bg-neon-violet/90 font-bold">
                Upgrade Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pages List */}
      {pages.length === 0 ? (
        <Card className="bg-void-paper border-white/10">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className="text-white font-heading font-semibold text-xl mb-2">No landing pages yet</h3>
            <p className="text-gray-400 mb-6">Create your first landing page to start capturing leads.</p>
            <Button 
              onClick={openCreateDialog}
              className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold"
            >
              Create Your First Landing Page
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {pages.map((page) => (
            <Card key={page.id} className="bg-void-paper border-white/10 card-hover">
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Page Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-heading font-semibold text-lg text-white truncate">
                        {page.title}
                      </h3>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        page.is_active 
                          ? 'bg-neon-mint/10 text-neon-mint' 
                          : 'bg-gray-500/10 text-gray-400'
                      }`}>
                        {page.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        page.theme === 'dark' ? 'bg-gray-700 text-gray-300' :
                        page.theme === 'light' ? 'bg-gray-200 text-gray-700' :
                        'bg-gradient-to-r from-neon-mint/20 to-neon-violet/20 text-white'
                      }`}>
                        {page.theme}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm mb-3 line-clamp-1">{page.headline}</p>
                    <div className="flex items-center gap-2">
                      <code className="px-2 py-1 bg-void rounded text-neon-mint text-sm">
                        /lp/{page.slug}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyPageLink(page.slug)}
                        className="text-gray-400 hover:text-white h-7"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Stats */}
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-gray-400 text-sm mb-1">
                        <Eye className="w-4 h-4" />
                        Views
                      </div>
                      <p className="font-mono font-bold text-white">{page.views}</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-gray-400 text-sm mb-1">
                        <Users className="w-4 h-4" />
                        Leads
                      </div>
                      <p className="font-mono font-bold text-neon-mint">{page.leads_count}</p>
                    </div>
                    <div className="text-center">
                      <div className="text-gray-400 text-sm mb-1">Conv.</div>
                      <p className="font-mono font-bold text-neon-amber">
                        {page.views > 0 ? ((page.leads_count / page.views) * 100).toFixed(1) : 0}%
                      </p>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <a href={`/lp/${page.slug}`} target="_blank" rel="noopener noreferrer">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-gray-400 hover:text-neon-mint"
                        title="Preview"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </a>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(page)}
                      className="text-gray-400 hover:text-white"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(page.id)}
                      className="text-gray-400 hover:text-red-400"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-void-paper border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingPage ? 'Edit Landing Page' : 'Create Landing Page'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* AI Generation Section */}
            {isPaidPlan && !editingPage && (
              <div className="p-4 bg-gradient-to-r from-neon-violet/10 to-neon-mint/10 rounded-lg border border-neon-violet/30">
                <div className="flex items-center gap-2 mb-3">
                  <Wand2 className="w-5 h-5 text-neon-violet" />
                  <span className="font-medium text-white">AI Content Generator</span>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={aiTopic}
                    onChange={(e) => setAiTopic(e.target.value)}
                    placeholder="Describe your product/service (e.g., 'SaaS tool for email marketing')"
                    className="bg-void border-white/10 flex-1"
                  />
                  <Button
                    type="button"
                    onClick={generateAIContent}
                    disabled={generatingContent || !aiTopic.trim()}
                    className="bg-neon-violet text-white hover:bg-neon-violet/90"
                  >
                    {generatingContent ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    <span className="ml-2">Generate</span>
                  </Button>
                </div>
                {isPowerPlan && (
                  <Button
                    type="button"
                    onClick={generateAIImage}
                    disabled={generatingImage || (!aiTopic.trim() && !formData.headline.trim())}
                    variant="outline"
                    className="mt-2 border-neon-mint/50 text-neon-mint hover:bg-neon-mint/10"
                  >
                    {generatingImage ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ImageIcon className="w-4 h-4 mr-2" />}
                    Generate Hero Image
                  </Button>
                )}
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Page Title (Internal)</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="My Product Launch"
                  required
                  className="bg-void border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label>Theme</Label>
                <Select
                  value={formData.theme}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, theme: value }))}
                >
                  <SelectTrigger className="bg-void border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-void-paper border-white/10">
                    <SelectItem value="dark">Dark</SelectItem>
                    <SelectItem value="light">Light</SelectItem>
                    <SelectItem value="gradient">Gradient</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Headline</Label>
              <Input
                value={formData.headline}
                onChange={(e) => setFormData(prev => ({ ...prev, headline: e.target.value }))}
                placeholder="Get More Traffic Today!"
                required
                className="bg-void border-white/10"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Subheadline (Optional)</Label>
              <Input
                value={formData.subheadline}
                onChange={(e) => setFormData(prev => ({ ...prev, subheadline: e.target.value }))}
                placeholder="Join 10,000+ marketers already growing"
                className="bg-void border-white/10"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe your offer, product, or service..."
                required
                rows={3}
                className="bg-void border-white/10 resize-none"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>CTA Button Text</Label>
                <Input
                  value={formData.cta_text}
                  onChange={(e) => setFormData(prev => ({ ...prev, cta_text: e.target.value }))}
                  placeholder="Get Started"
                  required
                  className="bg-void border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label>Redirect URL (After Submit)</Label>
                <Input
                  type="url"
                  value={formData.redirect_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, redirect_url: e.target.value }))}
                  placeholder="https://your-site.com/thank-you"
                  className="bg-void border-white/10"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hero Image URL</Label>
                <Input
                  value={formData.hero_image_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, hero_image_url: e.target.value }))}
                  placeholder="https://example.com/image.jpg or AI generated"
                  className="bg-void border-white/10"
                />
                {formData.hero_image_url && (
                  <div className="mt-2 rounded-lg overflow-hidden bg-void border border-white/10">
                    <img 
                      src={formData.hero_image_url} 
                      alt="Hero preview" 
                      className="w-full h-24 object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        // Clear the invalid URL
                        if (formData.hero_image_url.startsWith('data:') || formData.hero_image_url.startsWith('blob:')) {
                          setFormData(prev => ({ ...prev, hero_image_url: '' }));
                          toast.error('Image failed to load');
                        }
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Video URL (YouTube/Vimeo)</Label>
                <Input
                  type="url"
                  value={formData.video_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                  placeholder="https://youtube.com/watch?v=..."
                  className="bg-void border-white/10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Thank You Message</Label>
              <Textarea
                value={formData.thank_you_message}
                onChange={(e) => setFormData(prev => ({ ...prev, thank_you_message: e.target.value }))}
                placeholder="Thanks for signing up!"
                rows={2}
                className="bg-void border-white/10 resize-none"
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-void rounded-lg">
              <div>
                <Label>Collect Phone Number</Label>
                <p className="text-gray-500 text-sm">Add phone field to lead form</p>
              </div>
              <Switch
                checked={formData.collect_phone}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, collect_phone: checked }))}
              />
            </div>
            
            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDialogOpen(false)}
                className="text-gray-400"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : editingPage ? (
                  'Save Changes'
                ) : (
                  'Create Landing Page'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Wrap with error boundary for production safety
const LandingPagesWithErrorBoundary = () => (
  <LandingPagesErrorBoundary>
    <LandingPages />
  </LandingPagesErrorBoundary>
);

export default LandingPagesWithErrorBoundary;
